import { useState } from 'react';
import { Link } from 'react-router';
import { getToken } from '../api/client.js';
import { DataTable, ErrorState, Field, Spinner } from '../components/ui.jsx';
import { StatTile } from '../components/charts.jsx';
import { useFetch, useTitle } from '../lib/hooks.js';
import { formatDateTime, formatXaf } from '../lib/format.js';
import { useAuth } from '../state/AuthContext.jsx';
import { useToast } from '../state/ToastContext.jsx';

/*
 * The money log: every payment, charge, bill, waiver, cancellation and refund,
 * written by the database as it happened and never changed afterwards. The
 * landlord reads their own books here, an administrator everybody's, and a
 * tenant or utility customer their own payment history.
 */

export const MONEY_KIND = {
  payment_received: { label: 'Payment received', tone: 'in' },
  payment_cancelled: { label: 'Receipt cancelled', tone: 'fix' },
  payment_erased: { label: 'Payment removed with its lease', tone: 'fix' },
  charge_added: { label: 'Charge added', tone: 'owed' },
  charge_waived: { label: 'Charge waived', tone: 'fix' },
  charge_changed: { label: 'Charge changed', tone: 'owed' },
  utility_bill_issued: { label: 'Utility bill issued', tone: 'owed' },
  utility_bill_withdrawn: { label: 'Utility bill withdrawn', tone: 'fix' },
  utility_bill_changed: { label: 'Utility bill corrected', tone: 'owed' },
  utility_bill_cancelled: { label: 'Utility bill cancelled', tone: 'fix' },
  utility_bill_waived: { label: 'Utility bill waived', tone: 'fix' },
  utility_bill_removed: { label: 'Utility bill removed', tone: 'fix' },
  utility_late_fee_added: { label: 'Utility late fee added', tone: 'owed' },
  utility_late_fee_waived: { label: 'Utility late fee waived', tone: 'fix' },
  utility_payment_received: { label: 'Utility payment received', tone: 'in' },
  utility_payment_cancelled: { label: 'Utility receipt cancelled', tone: 'fix' },
  utility_payment_erased: { label: 'Utility payment removed with its account', tone: 'fix' },
  deposit_refunded: { label: 'Deposit refunded', tone: 'out' },
  utility_refunded: { label: 'Overpayment refunded', tone: 'out' },
  refund_cancelled: { label: 'Refund cancelled', tone: 'fix' },
  main_meter_charged: { label: 'Main meter bill from the utility', tone: 'owed' },
  main_meter_charge_changed: { label: 'Main meter bill corrected', tone: 'owed' },
  main_meter_charge_removed: { label: 'Main meter bill removed', tone: 'fix' },
  main_meter_paid: { label: 'Paid to the utility', tone: 'out' },
  main_meter_payment_cancelled: { label: 'Payment to the utility cancelled', tone: 'fix' }
};

const FILTERS = [
  ['all', 'Everything'],
  ['money_in', 'Money received'],
  ['money_out', 'Refunds'],
  ['charges', 'Charges and bills'],
  ['corrections', 'Cancellations and waivers'],
  ['main_meter', 'Main meter and the utility']
];

const PAGE_SIZE = 50;

const MoneyLog = () => {
  const { user } = useAuth();
  const toast = useToast();
  const own = !['landlord', 'manager', 'admin'].includes(user.role);
  useTitle(own ? 'Payment history' : 'Money log');

  const [filters, setFilters] = useState({ kind: 'all', from: '', to: '', q: '' });
  const [page, setPage] = useState(1);
  const params = new URLSearchParams(
    Object.entries({ ...filters, page, pageSize: PAGE_SIZE }).filter(([, value]) => value !== '' && value !== null)
  ).toString();
  const log = useFetch(`/money-log?${params}`, [params]);
  const set = (patch) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  // A plain link cannot carry the sign-in, so the file is fetched and handed over.
  const download = async () => {
    try {
      const response = await fetch(`/api/money-log?${params}&format=csv`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!response.ok) throw new Error('Could not build the spreadsheet.');
      const url = URL.createObjectURL(await response.blob());
      const link = Object.assign(document.createElement('a'), { href: url, download: `money-log-${new Date().toISOString().slice(0, 10)}.csv` });
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const columns = [
    { key: 'at', header: 'When', render: (row) => <span className="nowrap">{formatDateTime(row.at)}</span> },
    {
      key: 'what',
      header: 'What',
      render: (row) => (
        <div className="cell-stack">
          <strong>{MONEY_KIND[row.kind]?.label || row.kind}</strong>
          {row.note ? <span className="muted">{row.note}</span> : null}
        </div>
      )
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (row) => (
        <span className={`money-amount is-${MONEY_KIND[row.kind]?.tone || 'owed'}`}>
          {row.amount > 0 ? '+' : row.amount < 0 ? '-' : ''}
          {formatXaf(Math.abs(row.amount))}
        </span>
      )
    },
    {
      key: 'reference',
      header: 'Reference',
      render: (row) =>
        row.source === 'refund' ? (
          <Link to={`/refunds/${row.sourceId}`}>{row.reference}</Link>
        ) : row.source === 'payment' && !own ? (
          <Link to={`/receipts/${row.sourceId}`}>{row.reference}</Link>
        ) : (
          row.reference || <span className="muted">-</span>
        )
    },
    ...(own ? [] : [{ key: 'party', header: 'For', render: (row) => row.party?.name || <span className="muted">-</span> }]),
    ...(user.role === 'admin' ? [{ key: 'landlord', header: 'Landlord', render: (row) => row.landlord?.name || '-' }] : []),
    {
      key: 'by',
      header: 'Done by',
      render: (row) =>
        row.by ? (
          <span>
            {row.by.name}
            {row.by.role === 'manager' ? <span className="muted"> (manager)</span> : null}
          </span>
        ) : (
          <span className="muted">{row.fromRecords ? 'From the records' : 'The system'}</span>
        )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>{own ? 'Payment history' : 'Money log'}</h1>
          <p className="muted">
            {own
              ? 'Every payment you made, every charge and bill, and every refund - as it was recorded, never changed afterwards.'
              : 'Every payment, charge, bill, waiver, cancellation and refund, written down as it happened. Nothing here can be edited or deleted: a mistake is corrected by a further entry, and both stay.'}
          </p>
        </div>
        <button type="button" className="btn btn-outline" onClick={download}>
          Download spreadsheet
        </button>
      </header>

      <div className="money-filters">
        <Field label="Show">
          <select value={filters.kind} onChange={(event) => set({ kind: event.target.value })}>
            {FILTERS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="From">
          <input type="date" value={filters.from} onChange={(event) => set({ from: event.target.value })} />
        </Field>
        <Field label="To">
          <input type="date" value={filters.to} onChange={(event) => set({ to: event.target.value })} />
        </Field>
        <Field label="Search">
          <input
            type="search"
            placeholder={own ? 'Receipt number' : 'Name, receipt or reference'}
            value={filters.q}
            onChange={(event) => set({ q: event.target.value })}
          />
        </Field>
      </div>

      {log.error ? <ErrorState message={log.error} onRetry={log.reload} /> : null}
      {log.loading && !log.data ? <Spinner /> : null}
      {log.data ? (
        <>
          <div className="stat-row">
            <StatTile value={formatXaf(log.data.totals.received)} label={own ? 'You paid' : 'Received'} note="Cancelled receipts left out" />
            <StatTile value={formatXaf(log.data.totals.refunded)} label="Refunded" note="Cancelled refunds left out" />
            <StatTile value={log.data.total.toLocaleString('en')} label="Entries" note="Matching what is shown" />
          </div>

          <DataTable
            columns={columns}
            rows={log.data.items}
            rowKey={(row) => row.id}
            empty={{ title: 'Nothing recorded yet', message: 'Payments, bills and refunds appear here as they happen.' }}
          />

          {log.data.total > PAGE_SIZE ? (
            <div className="row-actions pager">
              <button type="button" className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                Newer
              </button>
              <span className="muted">
                Page {page} of {Math.ceil(log.data.total / PAGE_SIZE)}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={page * PAGE_SIZE >= log.data.total}
                onClick={() => setPage(page + 1)}
              >
                Older
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default MoneyLog;
